/**
 * ResearchInput - Two-step input for research queries
 *
 * For authenticated users: Direct query submission
 * For visitors: Query input → Email capture with Turnstile
 *
 * Uses the "sunk cost" principle: users who invest effort typing
 * a query are more likely to complete the email capture step.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ArrowRight, Sparkles, Mail, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IntensitySelectorInline } from './IntensitySelector';
import { ResearchModeSelector } from './ResearchModeSelector';
import type { ResearchIntensity } from '@/types';
import { INTENSITY_CONFIGS } from '@/types';
import type { ResearchTemplate } from './TemplateSelector';
import type { ResearchMode } from '@/types/researchModes';
import { RESEARCH_MODE_CONFIGS } from '@/types/researchModeConfigs';

// Rotating placeholder prompts — steer toward Financial & Strategic (highest win rates)
const PLACEHOLDER_PROMPTS = [
  'Is NVDA overvalued at current multiples?',
  'Should my B2B SaaS expand to enterprise in Year 2?',
  'What are the structural risks of investing in Chinese tech?',
  'Build a go-to-market strategy for AI in healthcare',
  'Compare TSLA vs BYD for long-term EV investment',
  'What does the evidence say about GLP-1 drugs for weight loss?',
];

// Cloudflare Turnstile site key
const TURNSTILE_SITE_KEY =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_TURNSTILE_SITE_KEY ||
  '0x4AAAAAAACMz1Tu4leCAjSyP';

// Skip Turnstile in development mode (localhost)
const SKIP_TURNSTILE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

type InputStage = 'query' | 'email';

interface ResearchInputProps {
  /** Called when query is submitted (authenticated users) */
  onSubmitAuth?: (query: string, intensity: ResearchIntensity, mode: ResearchMode) => void;
  /** Called when free-tier submission is made (legacy email flow) */
  onSubmitFree?: (query: string, email: string, turnstileToken: string, intensity: ResearchIntensity, mode: ResearchMode) => Promise<void>;
  /** Called when fingerprint-based query is submitted (Sealed Verdict flow) */
  onSubmitFingerprint?: (query: string, fingerprintId: string, turnstileToken: string, intensity: ResearchIntensity, mode: ResearchMode) => Promise<void>;
  /** Browser fingerprint ID for Sealed Verdict flow */
  fingerprintId?: string | null;
  /** Whether this is the user's first fingerprint query (full access) */
  isFirstQuery?: boolean;
  /** Whether user is authenticated */
  isAuthenticated?: boolean;
  /** Whether submission is in progress */
  isSubmitting?: boolean;
  /** Whether research is currently active (disables input to prevent duplicate submissions) */
  isResearchActive?: boolean;
  /** External error message to display */
  error?: string | null;
  className?: string;
  /** User's current credit balance for intensity restrictions */
  creditBalance?: number | null;
  /** Whether user has unlimited credits */
  isUnlimited?: boolean;
  /** Show intensity selector (default: true) */
  showIntensitySelector?: boolean;
  /** Show mode selector (default: true) */
  showModeSelector?: boolean;
  /** Selected research template (formats query before submission) */
  selectedTemplate?: ResearchTemplate | null;
}

export function ResearchInput({
  onSubmitAuth,
  onSubmitFree,
  onSubmitFingerprint,
  fingerprintId,
  isFirstQuery: _isFirstQuery = true,
  isAuthenticated = false,
  isSubmitting = false,
  isResearchActive = false,
  error,
  className,
  creditBalance,
  isUnlimited = false,
  showIntensitySelector = true,
  showModeSelector = true,
  selectedTemplate,
}: ResearchInputProps) {
  // Determine which flow to use
  // - Authenticated: direct query submission
  // - Fingerprint + first query: no email required
  // - Fingerprint + not first query: will show sealed overlay (handled by parent)
  // - No fingerprint: legacy email flow
  // Note: _isFirstQuery is passed but not used here - parent handles sealed state display
  const useFingerprint = !!fingerprintId && !!onSubmitFingerprint;
  const [query, setQuery] = useState('');
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<InputStage>('query');
  const [isFocused, setIsFocused] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [localError, setLocalError] = useState('');
  const [intensity, setIntensity] = useState<ResearchIntensity>('fast');
  const [mode, setMode] = useState<ResearchMode>('auto');

  // Get current intensity config for display
  const currentIntensityConfig = INTENSITY_CONFIGS[intensity];
  const currentModeConfig = RESEARCH_MODE_CONFIGS[mode];

  // Rotating placeholder
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  useEffect(() => {
    if (query || isFocused) return; // pause rotation when user is typing/focused
    const id = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % PLACEHOLDER_PROMPTS.length);
    }, 4000);
    return () => clearInterval(id);
  }, [query, isFocused]);
  const activePlaceholder = selectedTemplate?.placeholder || PLACEHOLDER_PROMPTS[placeholderIdx];

  // Character limit for free (unauthenticated) queries
  const FREE_QUERY_CHAR_LIMIT = 200;
  const charLimit = isAuthenticated ? undefined : FREE_QUERY_CHAR_LIMIT;

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  // Auto-resize textarea to fit content
  const autoResize = useCallback(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    // Reset height to auto so scrollHeight recalculates correctly
    textarea.style.height = 'auto';
    // Cap at ~5 lines (120px), min 1 line
    const maxHeight = 120;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  // Auto-focus query input
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Resize on query change
  useEffect(() => {
    autoResize();
  }, [query, autoResize]);

  // Load Turnstile when needed:
  // - Email stage (legacy flow)
  // - Fingerprint flow (needs turnstile for bot protection on query submit)
  const needsTurnstile = !isAuthenticated && (stage === 'email' || useFingerprint);

  useEffect(() => {
    if (!needsTurnstile) return;

    // Skip Turnstile in development - set a dummy token
    if (SKIP_TURNSTILE) {
      setTurnstileToken('dev-bypass-token');
      if (stage === 'email') {
        setTimeout(() => emailInputRef.current?.focus(), 200);
      }
      return;
    }

    const scriptId = 'turnstile-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    const renderWidget = () => {
      if (turnstileRef.current && window.turnstile && !turnstileWidgetId.current) {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          theme: 'dark',
          size: 'compact',
        });
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = () => setTimeout(renderWidget, 100);
      document.body.appendChild(script);
    } else if (window.turnstile) {
      setTimeout(renderWidget, 100);
    }

    // Focus email input (only for email stage)
    if (stage === 'email') {
      setTimeout(() => emailInputRef.current?.focus(), 200);
    }

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch {
          // Ignore errors
        }
        turnstileWidgetId.current = null;
      }
    };
  }, [needsTurnstile, stage]);

  const handleQuerySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isResearchActive || isSubmitting) return;

    // Format query using template if selected
    const formattedQuery = selectedTemplate
      ? selectedTemplate.formatQuery(query.trim())
      : query.trim();

    if (isAuthenticated) {
      // Authenticated flow - direct submission
      onSubmitAuth?.(formattedQuery, intensity, mode);
    } else if (useFingerprint && fingerprintId) {
      // Fingerprint flow - submit with fingerprint (no email required for 1st query)
      if (!turnstileToken && !SKIP_TURNSTILE) {
        setLocalError('Please complete the verification');
        return;
      }
      setLocalError('');
      try {
        await onSubmitFingerprint?.(formattedQuery, fingerprintId, turnstileToken, intensity, mode);
      } catch (err) {
        // Error is handled by parent via the error prop
      }
    } else {
      // Legacy email flow - go to email stage
      setStage('email');
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !turnstileToken) {
      setLocalError(!turnstileToken ? 'Please complete the verification' : 'Please enter your email');
      return;
    }

    // Format query using template if selected
    const formattedQuery = selectedTemplate
      ? selectedTemplate.formatQuery(query.trim())
      : query.trim();

    setLocalError('');
    try {
      await onSubmitFree?.(formattedQuery, email.trim(), turnstileToken, intensity, mode);
    } catch (err) {
      // Error is handled by parent via the error prop
    }
  };

  const displayError = error || localError;

  return (
    <div className={cn('relative w-full group', className)}>
      {/* Main input container */}
      {stage === 'query' ? (
        <>
          {/* Input box with gradient border */}
          <div className="relative">
            {/* Ambient glow - only on focus */}
            <div
              className={cn(
                'absolute -inset-3 rounded-2xl transition-all duration-700 pointer-events-none',
                'bg-gradient-to-r from-verdict-primary/0 via-verdict-primary/15 to-verdict-primary/0',
                'blur-2xl',
                isFocused ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
              )}
            />

            {/* Gradient border wrapper - only wraps the form */}
            <div
              className={cn(
                'relative rounded-2xl p-[1px] transition-all duration-500',
                isFocused
                  ? 'bg-gradient-to-r from-verdict-primary/40 via-verdict-primary/70 to-verdict-primary/40'
                  : 'bg-surface-raised/50 group-hover:bg-surface-raised/60'
              )}
            >
              <form
                onSubmit={handleQuerySubmit}
                className="relative bg-surface overflow-hidden rounded-[15px]"
              >
                {/* Textarea area */}
                <div className="flex items-start px-4 sm:px-5 pt-4 sm:pt-5 pb-2 w-full">
                  <Search className={cn('w-5 h-5 mt-1 shrink-0 transition-colors duration-300', isFocused ? 'text-verdict-primary' : 'text-text-muted')} />
                  <textarea
                    ref={inputRef}
                    value={query}
                    onChange={(e) => {
                      const value = charLimit ? e.target.value.slice(0, charLimit) : e.target.value;
                      setQuery(value);
                    }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleQuerySubmit(e);
                      }
                    }}
                    placeholder={activePlaceholder}
                    maxLength={charLimit}
                    rows={1}
                    aria-label="Research query"
                    aria-describedby={charLimit ? "query-char-count" : undefined}
                    className="flex-1 min-w-0 bg-transparent border-none text-text-primary placeholder-text-muted text-base sm:text-lg pl-3 focus:outline-none resize-none leading-relaxed"
                    style={{ overflow: 'hidden' }}
                  />
                </div>
                {/* Bottom action bar */}
                <div className="flex items-center justify-between px-4 sm:px-5 py-2.5 border-t border-border">
                  <div className="flex items-center gap-2">
                    {charLimit && query.length > 0 ? (
                      <span
                        id="query-char-count"
                        className={cn(
                          'text-xs tabular-nums transition-colors',
                          query.length >= charLimit ? 'text-red-400' : query.length >= charLimit * 0.8 ? 'text-amber-400' : 'text-text-muted'
                        )}
                        role="status"
                        aria-live="polite"
                      >
                        {query.length}/{charLimit}
                      </span>
                    ) : (
                      <span className="text-[11px] text-text-muted tracking-wide">
                        Shift+Enter for new line
                      </span>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={!query.trim() || isResearchActive || isSubmitting || (useFingerprint && !turnstileToken && !SKIP_TURNSTILE)}
                    className={cn(
                      'flex items-center justify-center gap-2 px-4 sm:px-5 py-2 rounded-xl transition-all duration-300 font-medium text-sm shrink-0',
                      query.trim() && !isResearchActive && !isSubmitting && (!useFingerprint || turnstileToken || SKIP_TURNSTILE)
                        ? 'bg-verdict-primary text-black hover:bg-verdict-primary/90 shadow-md shadow-verdict-primary/20'
                        : 'bg-surface-light text-text-muted cursor-not-allowed'
                    )}
                  >
                    {isResearchActive || isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5 shrink-0" />
                    )}
                    <span className="hidden xs:inline">
                      {isResearchActive ? 'Researching...' : isSubmitting ? 'Starting...' : 'Investigate'}
                    </span>
                  </button>
                </div>
                {/* Turnstile widget for fingerprint flow (hidden in development) */}
                {useFingerprint && !SKIP_TURNSTILE && (
                  <div ref={turnstileRef} className="px-4 pb-3 flex justify-center" />
                )}
                {useFingerprint && SKIP_TURNSTILE && (
                  <p className="text-xs text-green-500 px-4 pb-2 text-center">Dev mode: Turnstile bypassed</p>
                )}
                {/* Error display */}
                {displayError && <p className="text-red-400 text-sm px-4 pb-3 text-center">{displayError}</p>}
              </form>
            </div>
          </div>

          {/* Mode Selector (Research Blueprint) - outside the border wrapper */}
          {showModeSelector && (
            <div className="flex flex-col items-center gap-2 mt-4">
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <span>Research Mode:</span>
                <span className="text-verdict-primary font-medium">
                  {currentModeConfig.name}
                </span>
              </div>
              <ResearchModeSelector
                value={mode}
                onChange={setMode}
                disabled={isSubmitting || isResearchActive}
                variant="pills"
              />
            </div>
          )}

          {/* Intensity Selector - outside the border wrapper */}
          {showIntensitySelector && (
            <div className="flex flex-col items-center gap-2 mt-4">
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <span>Research Depth:</span>
                <span className="text-verdict-primary font-medium">
                  {currentIntensityConfig.label}
                </span>
                <span className="text-text-muted">
                  ({currentIntensityConfig.agentCount} agents)
                </span>
              </div>
              <IntensitySelectorInline
                value={intensity}
                onChange={setIntensity}
                creditBalance={creditBalance}
                isUnlimited={isUnlimited}
                isAuthenticated={isAuthenticated}
                disabled={isSubmitting || isResearchActive}
              />
            </div>
          )}
        </>
      ) : (
        /* Email stage - also gets gradient border */
        <div className="relative">
          <div
            className={cn(
              'absolute -inset-3 rounded-2xl pointer-events-none',
              'bg-gradient-to-r from-verdict-primary/0 via-verdict-primary/15 to-verdict-primary/0',
              'blur-2xl opacity-80'
            )}
          />
          <div className="relative rounded-2xl p-[1px] bg-gradient-to-r from-verdict-primary/30 via-verdict-primary/50 to-verdict-primary/30">
            <form
              onSubmit={handleEmailSubmit}
              className="relative bg-surface rounded-[15px] p-5"
            >
              {/* Query display */}
              <p className="text-sm text-text-secondary mb-3 line-clamp-1">
                Researching: <span className="text-text-primary">"{query}"</span>
              </p>
              {/* Email input row */}
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-text-muted shrink-0" />
                <input
                  ref={emailInputRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email to receive the report"
                  className="flex-1 bg-transparent border-none text-text-primary placeholder-text-muted text-base py-2 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={!email.trim() || !turnstileToken || isSubmitting}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 font-medium text-sm shrink-0',
                    email.trim() && turnstileToken && !isSubmitting
                      ? 'bg-verdict-primary text-black hover:bg-verdict-primary/90 shadow-md shadow-verdict-primary/20'
                      : 'bg-surface-light text-text-muted'
                  )}
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
              {/* Turnstile widget (hidden in development) */}
              {!SKIP_TURNSTILE && <div ref={turnstileRef} className="mt-3" />}
              {SKIP_TURNSTILE && <p className="text-xs text-green-500 mt-2">Dev mode: Turnstile bypassed</p>}
              {/* Error display */}
              {displayError && <p className="text-red-400 text-sm mt-2">{displayError}</p>}
              {/* Back button */}
              <button
                type="button"
                onClick={() => setStage('query')}
                className="text-xs text-text-muted hover:text-text-secondary mt-2 transition-colors"
              >
                Edit query
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

